<template>
    <cic-popup
        :title="item.providerId ? 'Edit Identity Provider' : 'New Identity Provider'"
        :saveable="enabled"
        @blur="$emit('blur')"
        @input="$emit('input')"
    >
        <div class="item-form">
            <ul>
                <li class="form-label-first">
                    {{ $t('Name') }}
                </li>
                <li>
                    <cic-textbox
                        id="provider_name"
                        :value="item.name"
                        :enabled="enabled"
                        @input="(val) => $emit('updateFieldValue', 'name', val)"
                    />
                </li>
                <li class="form-label">
                    {{ $t('Allow use by suborganisations') }}
                </li>
                <li>
                    <cic-checkbox
                        id="provider_suborgs"
                        :value="item.allowForSubOrgs"
                        :enabled="enabled"
                        @input="(val) => $emit('updateFieldValue', 'allowForSubOrgs', val)"
                    />
                </li>
                <li class="form-label">
                    {{ $t('ACS binding') }}
                </li>
                <li>
                    <!-- may have multiple settings in the future -->
                    <!--<select v-if="enabled" v-model="item.acsBinding" class="ui-select">
                        <option value="urn:oasis:names:tc:SAML:2.0:bindings:HTTP-POST">{{ $t('urn:oasis:names:tc:SAML:2.0:bindings:HTTP-POST') }}</option>
                    </select>-->
                    <span>{{ $t(item.acsBinding) }}</span>
                </li>
                <li class="form-label">
                    {{ $t('Use NameID as SAML attribute name') }}
                </li>
                <li>
                    <cic-checkbox
                        id="use_nameid"
                        :value="item.samlName === 'NameID'"
                        :enabled="enabled"
                        @input="setNameID"
                    />
                </li>
                <li v-if="item.samlName !== 'NameID'" class="form-label">
                    {{ $t('SAML attribute name') }}
                </li>
                <li v-if="item.samlName !== 'NameID'">
                    <cic-textbox
                        id="provider_saml_name"
                        :value="item.samlName"
                        :enabled="enabled"
                        @input="(val) => $emit('updateFieldValue', 'samlName', val)"
                    />
                </li>
                <li class="form-label">
                    {{ $t('NameID format') }}
                </li>
                <li>
                    <cic-select
                        id="provider_name_format"
                        :value="item.nameIdFormat"
                        :enabled="enabled"
                        :options="nameIdFormatOptions"
                        @input="(val) => $emit('updateFieldValue', 'nameIdFormat', val)"
                    />
                </li>
                <li class="form-label">
                    {{ $t('Entity ID') }}
                </li>
                <li>
                    <cic-textbox
                        id="provider_entity_id"
                        :value="item.entityId"
                        :enabled="enabled"
                        @input="(val) => $emit('updateFieldValue', 'entityId', val)"
                    />
                </li>
                <!-- may need to specify in the future, but can determine from sso binding for now -->
                <!--<li class="form-label">{{ $t('SSO binding method') }}</li>
                <li><cic-textbox v-model="item.ssoBindingMethod" :enabled="enabled" /></li>-->
                <li class="form-label">
                    {{ $t('SSO binding') }}
                </li>
                <li>
                    <!-- may have multiple settings in the future -->
                    <!--<select v-if="enabled" v-model="item.ssoBinding" class="ui-select">
                        <option>urn:oasis:names:tc:SAML:2.0:bindings:HTTP-POST</option>
                    </select>-->
                    <span>{{ $t(item.ssoBinding) }}</span>
                </li>
                <li class="form-label">
                    {{ $t('Authentication Context') }}
                </li>
                <li>
                    <cic-select
                        id="provider_auth_context"
                        :value="item.authContext"
                        :enabled="enabled"
                        :options="authContextOptions"
                        @input="(val) => $emit('updateFieldValue', 'authContext', val)"
                    />
                </li>
                <li class="form-label">
                    {{ $t('SSO URL') }}
                </li>
                <li>
                    <cic-textbox
                        id="provider_sso_url"
                        :value="item.ssoUrl"
                        :enabled="enabled"
                        @input="(val) => $emit('updateFieldValue', 'ssoUrl', val)"
                    />
                </li>
                <li class="form-label">
                    {{ $t('x509 certificate') }}
                </li>
                <li>
                    <input v-if="enabled" id="certificate" type="file" @change="importFile" />
                </li>
                <li class="form-label">
                    {{ $t('Allow identity provider initiated SSO') }}
                </li>
                <li>
                    <cic-checkbox
                        id="provider_initiate"
                        :value="item.allowInitiateSso"
                        :enabled="enabled"
                        @input="(val) => $emit('updateFieldValue', 'allowInitiateSso', val)"
                    />
                </li>
                <!-- may have multiple settings in the future -->
                <!--
                <template v-if="item.allowInitiateSso">
                    <li class="form-label">{{ $t('Product') }}</li>
                    <li>
                        <select
                            v-if="enabled"
                            v-model="item.product"
                            class="ui-select">
                            <option v-for="product in products" :value="product">{{ $t(product) }}</option>
                        </select>
                        <span v-else>{{ $t(item.product) }}</span>
                    </li>
                </template>-->
            </ul>
        </div>
    </cic-popup>
</template>

<script>
    import CicPopup from '@/components/CicPopup';
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import CicSelect from '@/components/inputs/CicSelect';
    import CicTextbox from '@/components/inputs/CicTextbox';
    import File from '@/mixins/File';
    import { showErrorMessage } from '@/utils/Notification';

    export default {
        name: 'EditIdentityProvider',
        components: {
            CicCheckbox,
            CicPopup,
            CicSelect,
            CicTextbox,
        },
        mixins: [File],
        props: {
            item: {
                type: Object,
                required: true,
            },
            enabled: {
                type: Boolean,
                default: true,
            },
        },
        emits: ['blur', 'input', 'updateFieldValue'],
        computed: {
            authContextOptions() {
                let options = [
                    'urn:oasis:names:tc:SAML:2.0:ac:classes:unspecified',
                    'urn:oasis:names:tc:SAML:2.0:ac:classes:Password',
                    'urn:oasis:names:tc:SAML:2.0:ac:classes:PasswordProtectedTransport',
                    'urn:oasis:names:tc:SAML:2.0:ac:classes:X509',
                    'urn:oasis:names:tc:SAML:2.0:ac:classes:Smartcard',
                    'urn:oasis:names:tc:SAML:2.0:ac:classes:Kerberos',
                    'urn:federation:authentication:windows',
                    'urn:oasis:names:tc:SAML:2.0:ac:classes:TLSClient',
                ];

                return [{ value: '', label: this.$t('Default') }].concat(
                    options.map((x) => {
                        return { value: x, label: this.$t(x) };
                    }),
                );
            },

            nameIdFormatOptions() {
                let options = [
                    'urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified',
                    'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress',
                    'urn:oasis:names:tc:SAML:1.1:nameid-format:X509SubjectName',
                    'urn:oasis:names:tc:SAML:1.1:nameid-format:WindowsDomainQualifiedName',
                    'urn:oasis:names:tc:SAML:2.0:nameid-format:kerberos',
                    'urn:oasis:names:tc:SAML:2.0:nameid-format:entity',
                    'urn:oasis:names:tc:SAML:2.0:nameid-format:persistent',
                ];
                if (this.item.samlName !== 'NameID') {
                    options.push('urn:oasis:names:tc:SAML:2.0:nameid-format:transient');
                }

                return options.map((x) => {
                    return { value: x, label: this.$t(x) };
                });
            },
        },
        methods: {
            fileOnLoad(content) {
                this.$emit('updateFieldValue', 'certificate', content);
            },

            importFile(evt) {
                if (!this.readFile(evt)) {
                    showErrorMessage(this.$t('Error reading file'));
                }
            },

            setNameID(val) {
                if (val) {
                    this.$emit('updateFieldValue', 'samlName', 'NameID');
                } else {
                    this.$emit('updateFieldValue', 'samlName', '');
                }
            },
        },
    };
</script>
